import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, CheckCircle } from 'lucide-react';
import * as api from '../lib/api';
import { toast } from 'sonner';

export const InvitePeople = () => {
  const navigate = useNavigate();
  const linkRef = useRef(null);
  const pendingRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // One invite code at a time; retired and replaced after each successful copy.
  const requestLink = useCallback(() => {
    if (linkRef.current) return Promise.resolve(linkRef.current);
    if (!pendingRef.current) {
      pendingRef.current = api
        .createInvite()
        .then(({ code }) => {
          linkRef.current = `${window.location.origin}/invite/${code}`;
          return linkRef.current;
        })
        .catch((err) => {
          pendingRef.current = null;
          throw err;
        });
    }
    return pendingRef.current;
  }, []);

  // Fetch it up front so the copy happens inside the tap, without a network wait.
  useEffect(() => {
    requestLink().catch((err) => {
      console.warn('[invite] could not pre-create invite:', err?.response?.status, err?.message);
    });
  }, [requestLink]);

  const writeToClipboard = async (link) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        return true;
      }
      console.warn('[invite] clipboard api unavailable');
    } catch (err) {
      console.warn('[invite] clipboard api rejected:', err?.name, err?.message);
    }
    try {
      // Legacy path, shaped so iOS Safari also accepts it.
      const field = document.createElement('textarea');
      field.value = link;
      field.contentEditable = 'true';
      field.readOnly = false;
      field.style.position = 'fixed';
      field.style.top = '0';
      field.style.opacity = '0';
      document.body.appendChild(field);
      const range = document.createRange();
      range.selectNodeContents(field);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      field.setSelectionRange(0, link.length);
      const ok = document.execCommand('copy');
      selection.removeAllRanges();
      document.body.removeChild(field);
      if (!ok) console.warn('[invite] execCommand copy returned false');
      return ok;
    } catch (err) {
      console.warn('[invite] execCommand copy threw:', err?.name, err?.message);
      return false;
    }
  };

  const handleCopy = async () => {
    setBusy(true);
    try {
      const link = linkRef.current || (await requestLink());
      if (!(await writeToClipboard(link))) throw new Error('clipboard-unavailable');
      setCopied(true);
      toast.success('Connection link copied');
      setTimeout(() => setCopied(false), 2500);
      // That code is spent on whoever receives it, so line up a fresh one for the
      // next invite. No page refresh needed to invite a second person.
      linkRef.current = null;
      pendingRef.current = null;
      requestLink().catch((err) => {
        console.warn('[invite] could not pre-create next invite:', err?.response?.status, err?.message);
      });
    } catch (err) {
      console.warn('[invite] copy failed:', err?.name, err?.message, err?.response?.status);
      toast.error('Could not copy your connection link');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page" data-testid="invite-people-page">
      <header className="page-header">
        <div className="connections-header-top">
          <button className="back-btn" onClick={() => navigate('/profile')} aria-label="Back to profile" data-testid="invite-back">
            <ArrowLeft />
          </button>
          <h1 className="page-title">Connect with someone</h1>
        </div>
      </header>

      <div className="invite-block">
        <h2 className="invite-heading">Recommendations are better from people you trust</h2>
        <p className="invite-copy">
          Share your personal connection link with someone you know and trust. If they
          already use irecommend, they can use it to connect with you. If they&apos;re new,
          they can join through the same link and you&apos;ll be connected automatically.
        </p>

        <button
          className="invite-primary"
          onClick={handleCopy}
          disabled={busy}
          // testid intentionally unchanged: the label is UX copy, the mechanism is the invite API
          data-testid="copy-invite-link"
        >
          {copied ? <CheckCircle className="invite-btn-icon" /> : <Copy className="invite-btn-icon" />}
          {copied ? 'Connection link copied' : 'Copy connection link'}
        </button>

        <p className="invite-footnote">
          Each link works once, and creates a connection between the two of you.
        </p>
      </div>
    </div>
  );
};
